import { useEffect, useMemo, useState } from "react";
import { Button } from "../ui/button";
import { AlertCircle } from "lucide-react";
import { RefreshCw } from "lucide-react";

type DashboardFrameState = "loading" | "ready" | "error";

export default function DashboardFrame({
  url,
  onClose,
}: {
  url: string;
  onClose: () => void;
}) {
  const origin = useMemo(() => {
    try {
      return new URL(url).origin;
    } catch {
      return null;
    }
  }, [url]);

  const [state, setState] = useState<DashboardFrameState>("loading");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setState("loading");

    const onMessage = (ev: MessageEvent) => {
      if (origin && ev.origin !== origin) return;
      if (ev.data === "dashboard-ready") {
        setState("ready");
      }
    };
    window.addEventListener("message", onMessage);

    const overall = window.setTimeout(() => {
      setState((s) => (s === "loading" ? "error" : s));
    }, 12000);

    return () => {
      window.removeEventListener("message", onMessage);
      window.clearTimeout(overall);
    };
  }, [origin, reloadKey]);

  // Browser-rendered error pages (connection refused, X-Frame-Options block,
  // 404, etc.) still fire the iframe's onload event. Give the SPA a short
  // window after onload to send its ready beacon — if it doesn't, treat the
  // load as a failure.
  const handleIframeLoad = () => {
    window.setTimeout(() => {
      setState((s) => (s === "loading" ? "error" : s));
    }, 3000);
  };

  return (
    <div className="relative -m-4 h-[calc(100vh-178px)]">
      <iframe
        key={reloadKey}
        src={url}
        title="Workspace Dashboard"
        // The dashboard's terminal copies via navigator.clipboard, which a
        // cross-origin iframe only gets when the parent delegates it.
        allow="clipboard-write"
        onLoad={handleIframeLoad}
        className={`w-full h-full border-0 ${
          state === "error" ? "invisible" : ""
        }`}
      />
      {state === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center bg-background pointer-events-none">
          <div className="text-muted-foreground text-sm">
            Loading workspace dashboard…
          </div>
        </div>
      )}
      {state === "error" && (
        <div className="absolute inset-0 flex items-center justify-center bg-background">
          <div className="max-w-md text-center space-y-3 p-6">
            <AlertCircle className="mx-auto text-destructive" size={32} />
            <h3 className="text-lg font-semibold">
              Failed to load workspace dashboard
            </h3>
            <p className="text-sm text-muted-foreground break-all">
              The dashboard at <code className="font-mono">{url}</code> did not
              respond. It may not be deployed yet, or the workspace could still
              be starting up.
            </p>
            <div className="flex gap-2 justify-center pt-2">
              <Button
                variant="outline"
                onClick={() => setReloadKey((k) => k + 1)}
              >
                <RefreshCw size={16} />
                Retry
              </Button>
              <Button variant="outline" asChild>
                <a href={url} target="_blank" rel="noreferrer">
                  Open in new tab
                </a>
              </Button>
              <Button variant="ghost" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
